import type { MetadataItem } from '../tool-config-types';

export type HttpToolInput = {
  credentialId?: string;
  url: string;
  redirect?: number;
  options?: {
    method?: string;
    headers?: Record<string, string>;
    body?: string;
    formData?: string;
  };
};

export type HttpToolOutput = {
  statusCode?: number;
  status?: number;
  headers?: Record<string, string>;
  body?: string;
  error?: string;
};

type ValidatedHttpOutput = {
  request?: {
    method?: string;
    url?: string;
  };
  response?: {
    statusCode?: number;
    success?: boolean;
    error?: string;
  };
};

type ValidatedHttpInput = {
  query: string;
  credentialId?: string;
};

export const isHttpToolInput = (input: unknown): input is HttpToolInput => {
  if (typeof input !== 'object' || input === null) return false;
  return typeof (input as HttpToolInput).url === 'string';
};

const isValidatedHttpInput = (input: unknown): input is ValidatedHttpInput => {
  if (typeof input !== 'object' || input === null) return false;
  return typeof (input as ValidatedHttpInput).query === 'string';
};

export const isHttpToolOutput = (output: unknown): output is HttpToolOutput => {
  return typeof output === 'object' && output !== null;
};

const isValidatedHttpOutput = (
  output: unknown,
): output is ValidatedHttpOutput => {
  if (typeof output !== 'object' || output === null) return false;
  const obj = output as Record<string, unknown>;
  return (
    typeof obj.request !== 'undefined' || typeof obj.response !== 'undefined'
  );
};

type HttpRootLevelInput = {
  url: string;
  method: string;
};

const isHttpRootLevelInput = (input: unknown): input is HttpRootLevelInput => {
  if (typeof input !== 'object' || input === null) return false;
  const obj = input as HttpRootLevelInput;
  return typeof obj.url === 'string' && typeof obj.method === 'string';
};

export const extractHttpCredentialId = (input: unknown): string | undefined => {
  if (isHttpToolInput(input)) {
    return input.credentialId;
  }
  if (isValidatedHttpInput(input)) {
    return input.credentialId;
  }
  return undefined;
};

export const extractHttpMetadata = ({
  input,
  output,
}: {
  input: unknown;
  output?: unknown;
}): MetadataItem[] => {
  const metadata: MetadataItem[] = [];

  // Handle validated-http format (method and status in output)
  if (isValidatedHttpOutput(output)) {
    const method = output.request?.method;
    if (method) metadata.push({ value: method.toUpperCase() });

    const statusCode = output.response?.statusCode;
    if (typeof statusCode === 'number') {
      metadata.push({ label: 'Status', value: statusCode });
    } else if (output.response?.success === true) {
      metadata.push({ label: 'Status', value: 200 });
    } else if (output.response?.error) {
      metadata.push({ label: 'Status', value: 'Error' });
    }
    return metadata;
  }

  // Handle regular http-tool format (method in input.options, status in output)
  if (isHttpToolInput(input)) {
    const method = input.options?.method;
    if (method) metadata.push({ value: method.toUpperCase() });
  }

  if (isHttpToolOutput(output)) {
    const statusCode = output.statusCode ?? output.status;
    if (typeof statusCode === 'number') {
      metadata.push({ label: 'Status', value: statusCode });
    }
  }

  return metadata;
};

/**
 * Extracts metadata for http-payload-creator.
 * Output is the generated HttpRequestType: { url, method, headers?, body? }
 */
export const extractHttpPayloadCreatorMetadata = ({
  output,
}: {
  input: unknown;
  output?: unknown;
}): MetadataItem[] => {
  const metadata: MetadataItem[] = [];

  if (isHttpRootLevelInput(output)) {
    metadata.push({ value: output.method.toUpperCase() });
  }

  return metadata;
};

/**
 * Extracts metadata for http-executor.
 * Input shape: { url, method, headers?, body?, ... } (method at root).
 * Output shape: buildHttpStructuredOutput { request, response }.
 *
 * Reads the method from the input while the call is still streaming so the
 * card doesn't render a wrong default method badge before the output arrives.
 */
export const extractHttpExecutorMetadata = ({
  input,
  output,
}: {
  input: unknown;
  output?: unknown;
}): MetadataItem[] => {
  const metadata: MetadataItem[] = [];

  if (isValidatedHttpOutput(output)) {
    const method = output.request?.method;
    if (method) metadata.push({ value: method.toUpperCase() });

    const statusCode = output.response?.statusCode;
    if (typeof statusCode === 'number') {
      metadata.push({ label: 'Status', value: statusCode });
    } else if (output.response?.success === true) {
      metadata.push({ label: 'Status', value: 200 });
    } else if (output.response?.error) {
      metadata.push({ label: 'Status', value: 'Error' });
    }
    return metadata;
  }

  if (isHttpRootLevelInput(input)) {
    metadata.push({ value: input.method.toUpperCase() });
  }

  return metadata;
};
